import { RadioGroup } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/solid'
import { classNames } from '@progress/kendo-react-common'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { Popup } from '@progress/kendo-react-popup'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { getTranslation } from 'utils/language-helper'

function BookingPageLink() {
    const router = useRouter()
    const { translations } = useAppContext()
    const { CustomBookingPageModal } = useAuth()
    const { default_booking_url } = useTenantContext()
    const learn_anchor = useRef<HTMLButtonElement | null>(null)
    const [show_learn_info, setShowLearnInfo] = useState(false)
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig

    const sharing_options = [
        {
            name: getTranslation(
                'settings.booking_page_sharing_option1',
                translations
            ),
            value: true,
        },
        {
            name: getTranslation(
                'settings.booking_page_sharing_option2',
                translations
            ),
            value: false,
        },
    ]

    const link_options = [
        {
            index: 0,
            name: getTranslation(
                'settings.booking_page_embed_code',
                translations
            ),
            value: `<iframe src="${default_booking_url}" frameborder="no" id="myIframe" scrolling="no" color="" loading='lazy' render="bg-w-image" style="width:100%;"></iframe><script src=https://${NEXT_PUBLIC_STATIC_DOMAIN}/js/aot-embed.js" type="text/javascript"></script>`,
            icon: 'feather-copy',
        },
        {
            index: 1,
            name: getTranslation(
                'settings.booking_page_direct_link',
                translations
            ),
            value: `${default_booking_url}`,
            icon: 'feather-arrow-up-right',
        },
    ]

    const goto_buttons = [
        {
            name: 'settings.booking_page_goto_services',
            path: '/dashboard/services',
        },
        {
            name: 'settings.booking_page_goto_memberships',
            path: '/dashboard/memberships',
        },
    ]

    const [sharing_option, setSharingOption] = useState(sharing_options[0])
    const [link_index, setLinkIndex] = useState(link_options[0].index)
    const [link_option, setLinkOption] = useState(link_options[0])

    useEffect(() => {
        setLinkOption(link_options[link_index])
    }, [link_index])

    return (
        <div className="relative flex flex-col gap-4 p-5 w-full lg:w-wide">
            <div className="flex gap-2">
                <fieldset className="w-full lg:w-1/3 pb-3">
                    <Translation
                        render_as="label"
                        className="block text-lg"
                        htmlFor="sharing_method"
                        content_key="settings.booking_page_sharing_method"
                    />
                    <DropDownList
                        id="sharing_method"
                        className="bg-white py-1 mt-1 block w-full shadow-sm border border-gray-300 rounded-md"
                        iconClassName="feather-chevron-down"
                        data={sharing_options}
                        textField="name"
                        dataItemKey="value"
                        value={sharing_option}
                        popupSettings={{ height: 'h-full', className: "z-[102]" }}
                        onChange={(e) => {
                            setSharingOption(e.target.value)
                        }}
                        
                    />
                </fieldset>
            </div>

            <div className="flex gap-2">
                <fieldset className="w-full pb-3">
                    <div className="">
                        <Translation
                            render_as="label"
                            className="text-sm"
                            htmlFor="code_to_add"
                            content_key="settings.booking_page_link_to"
                        />
                        <span
                            className="text-primary-light ml-3"
                            ref={learn_anchor}
                            onMouseEnter={() => setShowLearnInfo(true)}
                            onMouseLeave={() => setShowLearnInfo(false)}
                        >
                            <i className="feather-info mr-1" />
                            <Translation
                                render_as="label"
                                content_key="settings.booking_page_learn_to"
                            />
                        </span>
                    </div>
                    {sharing_option.value ? (
                        <>
                            <div className="my-3">
                                <RadioGroup
                                    value={link_index}
                                    onChange={setLinkIndex}
                                >
                                    <RadioGroup.Label className="sr-only">
                                        <Translation content_key="settings.booking_page_link_to" />
                                    </RadioGroup.Label>

                                    <div className="flex gap-6">
                                        {link_options.map((op) => {
                                            return (
                                                <RadioGroup.Option
                                                    key={op.name}
                                                    value={op.index}
                                                    className={({
                                                        checked,
                                                    }) => {
                                                        return classNames(
                                                            'relative rounded-lg shadow-md px-2 py-1 cursor-pointer flex focus:outline-none',
                                                            checked
                                                                ? 'ring-primary-light ring-opacity-60 bg-primary-light text-primary'
                                                                : 'bg-gray-100'
                                                        )
                                                    }}
                                                >
                                                    {({ checked }) => {
                                                        return (
                                                            <>
                                                                <div className="flex items-center justify-between w-full">
                                                                    {checked ? (
                                                                        <div className="flex-shrink-0 text-white w-10">
                                                                            <CheckIcon className="w-6 h-6" />
                                                                        </div>
                                                                    ) : (
                                                                        ''
                                                                    )}
                                                                    <div className="flex flex-1 items-center">
                                                                        <div className="text-sm">
                                                                            <RadioGroup.Label
                                                                                as="p"
                                                                                className={`font-medium  ${
                                                                                    checked
                                                                                        ? 'text-white'
                                                                                        : 'text-primary'
                                                                                }`}
                                                                            >
                                                                                {
                                                                                    <Translation
                                                                                        content_key={
                                                                                            op.name
                                                                                        }
                                                                                    />
                                                                                }
                                                                            </RadioGroup.Label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )
                                                    }}
                                                </RadioGroup.Option>
                                            )
                                        })}
                                    </div>
                                </RadioGroup>
                            </div>
                            <div className="relative">
                                <textarea
                                    id="code_to_add"
                                    className="placeholder-gray-300 mt-1 h-40 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md px-6 py-3"
                                    value={link_option.value}
                                    onChange={() => {}}
                                />
                                <i
                                    className={classNames(
                                        'cursor-pointer text-lg text-primary-light bg-white absolute p-2 right-5 top-1',
                                        link_option.icon
                                    )}
                                    onClick={() => {
                                        if (
                                            link_option.name ==
                                            link_options[0].name
                                        ) {
                                            navigator.clipboard.writeText(
                                                link_option.value
                                            )
                                        } else {
                                            window
                                                .open(
                                                    link_option.value,
                                                    '_blank'
                                                )
                                                ?.focus()
                                        }
                                    }}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <Translation
                                render_as="div"
                                className="text-base text-gray-400 my-2"
                                content_key="settings.direct_link_instructions"
                            />
                            <div className="flex gap-3 text-white">
                                {goto_buttons.map((button) => {
                                    return (
                                        <button
                                            key={button.name}
                                            className="rounded-lg shadow-md px-2 py-1 cursor-pointer bg-primary-light"
                                            onClick={() => {
                                                CustomBookingPageModal.close()
                                                router.push(button.path)
                                            }}
                                        >
                                            <Translation
                                                content_key={button.name}
                                            />
                                        </button>
                                    )
                                })}
                            </div>
                        </>
                    )}
                </fieldset>
            </div>

            <Popup
                anchor={learn_anchor.current}
                show={show_learn_info}
                animate={false}
                popupAlign={{
                    horizontal: 'left',
                    vertical: 'top',
                }}
                popupClass={
                    'bg-black text-white shadow-lg rounded-xl w-96 px-5 py-3 mt-2'
                }
                className="z-[102]"
            >
                <Translation
                    render_as="p"
                    content_key="settings.booking_page_learn_to_desc"
                />
            </Popup>
        </div>
    )
}

export default BookingPageLink
